import React, { useState } from "react";
import { useLocation } from "wouter";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { getCsrfToken } from "@workspace/api-client-react";

export default function UnsubscribeConfirmPage() {
  useDocumentTitle("Confirm Unsubscribe");
  const [, navigate] = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");

  async function handleConfirm() {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/unsubscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-csrf-token": getCsrfToken() ?? "",
        },
        body: JSON.stringify({ token }),
      });
      if (res.ok || res.redirected) {
        navigate("/unsubscribe-success");
      } else {
        const data = await res.json().catch(() => ({}));
        setError((data as { error?: string }).error ?? "Something went wrong. Please try again.");
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex-1 flex items-center justify-center px-4 pt-24 pb-20">
      <div className="max-w-md w-full bg-zinc-900/50 border border-white/10 rounded-xl p-8 backdrop-blur text-center">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-5"
          style={{ background: "rgba(232,24,74,0.08)", border: "1px solid rgba(232,24,74,0.2)" }}
        >
          <svg
            className="w-6 h-6"
            style={{ color: "#E8184A" }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 11-12.728 12.728A9 9 0 0118.364 5.636z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l6 6M15 9l-6 6" />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-white uppercase tracking-tight mb-3">
          Unsubscribe from Emails
        </h1>

        {!token ? (
          <p className="text-zinc-400 font-sans text-sm leading-relaxed">
            This link is invalid or has expired. Please request a new unsubscribe link.
          </p>
        ) : (
          <>
            <p className="text-zinc-400 font-sans text-sm leading-relaxed mb-6">
              Are you sure you want to unsubscribe from platform update emails? You can
              re-enable them at any time from your dashboard.
            </p>

            {error && (
              <p className="text-red-400 text-sm mb-4">{error}</p>
            )}

            <div className="flex flex-col gap-3">
              <button
                onClick={handleConfirm}
                disabled={loading}
                className="inline-flex items-center justify-center h-10 px-6 text-white text-sm font-bold uppercase tracking-wide rounded-lg transition-colors disabled:opacity-50"
                style={{ background: "#E8184A" }}
              >
                {loading ? "Processing..." : "Confirm Unsubscribe"}
              </button>

              <a
                href="/dashboard"
                className="inline-flex items-center justify-center h-10 px-6 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-bold uppercase tracking-wide rounded-lg border border-white/10 transition-colors"
              >
                Cancel
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
